import axiosInstance from './axios'
import { ApiBuilder } from './ApiBuilder'
import type { ApiResponse, ServiceOptions } from '@/types/core/api'
import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import { AppError } from '@/types/core/errors'
import { messageService } from './MessageService'
import { getApiUrl } from '@/config/apiConfig'
import { logger } from '@/utils/logger'

export abstract class ApiService {
  protected basePath: string

  constructor(basePath: string) {
    this.basePath = basePath
  }

  // 🎯 新增 Fluent API Builder 方法
  config<T>(): ApiBuilder<T> {
    return new ApiBuilder<T>()
  }

  // 🎯 新的 request 方法 - 支援 ServiceOptions
  protected async requestWithOptions<T>(
    endpoint: string,
    axiosConfig: AxiosRequestConfig,
    options?: ServiceOptions<T>
  ): Promise<ApiResponse<T>> {
    let destroyLoading: (() => void) | undefined
    if (options?.loading) {
      destroyLoading = this.showLoading(options)
    }

    try {
      const fullEndpoint = `${this.basePath}${endpoint}`
      const url = getApiUrl(fullEndpoint)
      this.logApiStart(axiosConfig, url)

      const maxRetries = options?.error?.enableRetry ? (options.error.maxRetries ?? 3) : 0
      const apiResponse = await this.tryRequestWithRetry<T>(url, axiosConfig, options, maxRetries)

      await this.handleSuccess(apiResponse, options, axiosConfig, url)
      return apiResponse
    } catch (error: any) {
      await this.handleError(error, options, axiosConfig, endpoint)
      throw error
    } finally {
      if (options?.onFinally) {
        await options.onFinally()
      }
      if (destroyLoading && typeof destroyLoading === 'function') {
        destroyLoading()
      }
    }
  }

  private showLoading<T>(options: ServiceOptions<T>): () => void {
    // 判斷 loading 類型和設定
    let loadingType: 'api' | 'form' | 'default' = 'default'
    let loadingMsg = '載入中...'
    let completedMsg: string | undefined

    if (typeof options.loading === 'string') {
      loadingMsg = options.loading
    } else if (typeof options.loading === 'object' && options.loading !== null) {
      loadingType = options.loading.type ?? 'default'
      loadingMsg = options.loading.message ?? '載入中...'
      completedMsg = options.loading.completedMessage
    }

    // 如果沒有明確指定完成訊息，嘗試從 message 選項中獲取
    if (!completedMsg && options.message?.showSuccessMessage && typeof options.message.showSuccessMessage === 'string') {
      completedMsg = options.message.showSuccessMessage
    }

    // 根據類型選擇適當的 loading 方法
    switch (loadingType) {
      case 'api':
        return messageService.apiLoading(loadingMsg, completedMsg)
      case 'form':
        return messageService.formLoading(loadingMsg, completedMsg)
      default:
        return messageService.loading(loadingMsg, completedMsg)
    }
  }

  private logApiStart(axiosConfig: AxiosRequestConfig, url: string) {
    const method = axiosConfig.method ?? 'GET'
    logger.api(`${method} ${url}`)
    
    if (axiosConfig.data) {
      if (axiosConfig.data instanceof FormData) {
        logger.api('Request data (FormData)', {
          entries: Array.from(axiosConfig.data.entries())
        })
      } else {
        logger.api('Request data', { data: axiosConfig.data })
      }
    }
  }

  private async tryRequestWithRetry<T>(
    url: string,
    axiosConfig: AxiosRequestConfig,
    options: ServiceOptions<T> | undefined,
    maxRetries: number
  ): Promise<ApiResponse<T>> {
    let lastError: any
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response: AxiosResponse<ApiResponse<T>> = await axiosInstance({
          url,
          ...axiosConfig
        })
        this.logApiResponse(axiosConfig, url, response)
        const apiResponse = response.data
        if (!apiResponse.success) {
          throw new AppError(
            apiResponse.message ?? '請求失敗',
            apiResponse.code ?? 'API_ERROR',
            apiResponse.data
          )
        }
        return apiResponse
      } catch (error: any) {
        lastError = error
        if (attempt === maxRetries || !options?.error?.enableRetry) {
          throw error
        }
        if (options.error?.retryDelay) {
          await new Promise(resolve => setTimeout(resolve, options.error!.retryDelay))
        }
      }
    }
    throw lastError
  }

  private logApiResponse(
    axiosConfig: AxiosRequestConfig,
    url: string,
    response: AxiosResponse<any>
  ) {
    const method = axiosConfig.method ?? 'GET'
    logger.api(`${method} ${url} - Response ${response.status}`)
  }

  private async handleSuccess<T>(
    apiResponse: ApiResponse<T>,
    options: ServiceOptions<T> | undefined,
    axiosConfig: AxiosRequestConfig,
    url: string
  ) {
    if (options?.onSuccess) {
      await options.onSuccess(apiResponse.data)
    }
    if (options?.message?.showSuccessMessage && !options.loading) {
      const successMsg = typeof options.message.showSuccessMessage === 'string'
        ? options.message.showSuccessMessage
        : '操作成功'
      logger.api(`Success message: ${successMsg}`)
      messageService.success(`🎯 ${successMsg}`)
    }
  }

  private async handleError<T>(
    error: any,
    options: ServiceOptions<T> | undefined,
    axiosConfig: AxiosRequestConfig,
    endpoint: string
  ) {
    let appError: AppError
    if (error instanceof AppError) {
      appError = error
    } else if (error.response) {
      const { status, data } = error.response
      appError = new AppError(
        data?.message ?? `請求失敗 (${status})`,
        `HTTP_${status}`,
        data,       // details: 後端回傳的資料
        error       // originalError: 完整的原始錯誤對象
      )
      await this.handleHttpErrorStatus(status, data, options)
    } else if (error.request) {
      appError = new AppError(
        '網路連線失敗，請稍後再試',
        'NETWORK_ERROR',
        error
      )
    } else {
      appError = new AppError(
        error.message ?? '未知錯誤',
        'UNKNOWN_ERROR',
        error
      )
    }

    this.logApiError(appError, axiosConfig, endpoint)
    if (options?.onError) {
      await options.onError(appError)
    }
    // 檢查是否為平台認證過期（已由 axios 攔截器處理）
    const isPlatformAuthExpired = (window as any).__PLATFORM_AUTH_EXPIRED__
    const shouldShowError = options?.message?.showErrorMessage !== false && !isPlatformAuthExpired
    
    if (shouldShowError) {
      const errorMsg = typeof options?.message?.showErrorMessage === 'string'
        ? options.message.showErrorMessage
        : appError.message
      messageService.error(errorMsg)
    }
    throw appError
  }

  private async handleHttpErrorStatus<T>(
    status: number,
    data: any,
    options: ServiceOptions<T> | undefined
  ) {
    if (!options?.error) return
    if (status === 401 && options.error.handle401) {
      await options.error.handle401()
    } else if (status === 403 && options.error.handle403) {
      await options.error.handle403()
    } else if (status === 404 && options.error.handle404) {
      await options.error.handle404()
    } else if (status === 422 && options.error.handle422) {
      options.error.handle422(data?.errors ?? {})
    } else if (status === 500 && options.error.handle500) {
      await options.error.handle500()
    }
  }

  private logApiError(
    appError: AppError,
    axiosConfig: AxiosRequestConfig,
    endpoint: string
  ) {
    const method = axiosConfig.method ?? 'GET'
    const fullEndpoint = `${this.basePath}${endpoint}`
    const url = getApiUrl(fullEndpoint)
    
    logger.error(`API Error: ${method} ${url} - ${appError.code}: ${appError.message}`, {
      code: appError.code,
      details: appError.details,
      originalError: appError.originalError?.message || appError.originalError
    })
  }

  // 🎯 簡潔的 HTTP 方法封裝 - 統一使用 ServiceOptions
  protected async get<T>(
    endpoint: string, 
    params?: any, 
    options?: ServiceOptions<T>
  ): Promise<ApiResponse<T>> {
    return this.requestWithOptions<T>(endpoint, { method: 'GET', params }, options)
  }

  protected async post<T>(
    endpoint: string, 
    data?: any, 
    options?: ServiceOptions<T> & { headers?: Record<string, string> }
  ): Promise<ApiResponse<T>> {
    const axiosConfig: any = { method: 'POST', data }
    if (options?.headers) {
      axiosConfig.headers = options.headers
    }
    return this.requestWithOptions<T>(endpoint, axiosConfig, options)
  }

  protected async put<T>(
    endpoint: string, 
    data?: any, 
    options?: ServiceOptions<T>
  ): Promise<ApiResponse<T>> {
    return this.requestWithOptions<T>(endpoint, { method: 'PUT', data }, options)
  }

  protected async patch<T>(
    endpoint: string, 
    data?: any, 
    options?: ServiceOptions<T>
  ): Promise<ApiResponse<T>> {
    return this.requestWithOptions<T>(endpoint, { method: 'PATCH', data }, options)
  }

  protected async delete<T>(
    endpoint: string, 
    options?: ServiceOptions<T>
  ): Promise<ApiResponse<T>> {
    return this.requestWithOptions<T>(endpoint, { method: 'DELETE' }, options)
  }

  // 🎯 Blob 資料回應處理方法
  protected async getBlobResponse(
    endpoint: string,
    params?: any
  ): Promise<Blob> {
    const fullEndpoint = `${this.basePath}${endpoint}`
    const url = getApiUrl(fullEndpoint)
    
    logger.api(`📥 API Request: GET ${url}`, { params })
    
    try {
      const response = await axiosInstance.get(url, {
        params,
        responseType: 'blob',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      })
      
      logger.api(`📤 API Response: GET ${url} - Success`, { 
        status: response.status,
        contentType: response.headers['content-type']
      })
      
      return response.data
    } catch (error: any) {
      logger.error(`📤 API Error: GET ${url}`, { 
        error: error.message,
        status: error.response?.status
      })
      
      if (error.response?.status === 401) {
        throw new AppError('認證失敗', 'UNAUTHORIZED', error)
      } else if (error.response?.status === 403) {
        throw new AppError('無權限執行此操作', 'FORBIDDEN', error)
      } else if (error.response?.status === 404) {
        throw new AppError('資源不存在', 'NOT_FOUND', error)
      } else if (error.response?.status === 500) {
        throw new AppError('伺服器內部錯誤', 'INTERNAL_ERROR', error)
      } else {
        throw new AppError('匯出失敗', 'EXPORT_ERROR', error)
      }
    }
  }
}
